import types from '../mutation-types';
import TicketsAPI from '../../api/tickets';
import authAPI from '../../api/auth';

const createDefaultFilters = () => ({
  status: [],
  priority: [],
  team_id: [],
  assignee_id: [],
  contact_id: [],
  business_account_id: [],
  custom_attributes: {},
});
const normalizeView = view => (view === 'kanban' ? 'kanban' : 'list');

const state = {
  tickets: [],
  currentTicket: null,
  statuses: [],
  priorities: [],
  teams: [],
  users: [],
  contacts: [],
  slaPolicies: [],
  slaMonitoringData: {
    applied_slas: [],
    sla_events: [],
    tickets: [],
  },
  ticketHistory: [],
  // Legacy list filters alias kept for backward compatibility.
  filters: createDefaultFilters(),
  filtersByView: {
    list: createDefaultFilters(),
    kanban: createDefaultFilters(),
  },
  uiFlags: {
    isFetching: false,
    isUpdating: false,
    isCreating: false,
    isFetchingTicket: false,
    isFetchingHistory: false,
  },
  meta: {
    count: 0,
    currentPage: 1,
    totalPages: 1,
  },
};

export const getters = {
  getTickets: $state => $state.tickets,
  getCurrentTicket: $state => $state.currentTicket,
  getStatuses: $state => $state.statuses,
  getPriorities: $state => $state.priorities,
  getTeams: $state => $state.teams,
  getUsers: $state => $state.users,
  getContacts: $state => $state.contacts,
  getSlaPolicies: $state => $state.slaPolicies,
  getSlaMonitoringData: $state => $state.slaMonitoringData,
  getFilters: $state => $state.filters,
  getFiltersByView: $state => view => {
    const targetView = normalizeView(view);
    return $state.filtersByView[targetView] || createDefaultFilters();
  },
  getTicketHistory: $state => $state.ticketHistory,
  getUIFlags: $state => $state.uiFlags,
  getMeta: $state => $state.meta,
};

export const actions = {
  async getTickets({ commit, state: ticketState }, params = {}) {
    commit(types.SET_TICKETS_UI_FLAG, { isFetching: true });
    try {
      const filterView = normalizeView(params.filterView);
      const activeFilters =
        ticketState.filtersByView?.[filterView] || ticketState.filters;
      const filterParams = { ...activeFilters, ...params };
      delete filterParams.filterView;
      // Remove team_id if explicitly set to null in params (used by Kanban to exclude team filter)
      if (params.team_id === null) {
        delete filterParams.team_id;
      }
      const response = await TicketsAPI.getAllTickets(filterParams);
      const { tickets, meta } = response.data;

      commit(types.SET_TICKETS, tickets || []);
      if (meta && typeof meta === 'object') {
        commit(types.SET_TICKETS_META, meta);
      }

      return { tickets: tickets || [], meta: meta || {} };
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isFetching: false });
    }
  },

  async get({ dispatch }, params = {}) {
    return dispatch('getTickets', params);
  },

  async getStatuses({ commit }) {
    const response = await TicketsAPI.getStatuses();
    const { statuses } = response.data;
    commit(types.SET_TICKET_STATUSES, statuses || []);
  },

  async getPriorities({ commit }) {
    const response = await TicketsAPI.getPriorities();
    const { priorities } = response.data;
    commit(types.SET_TICKET_PRIORITIES, priorities || []);
  },

  async getTeams({ commit }) {
    const response = await TicketsAPI.getTeams();
    const { teams } = response.data;
    commit(types.SET_TICKET_TEAMS, teams || []);
  },

  async getUsers({ commit }) {
    const response = await TicketsAPI.getUsers();
    const { users } = response.data;
    commit(types.SET_TICKET_USERS, users || []);
  },

  async getContacts({ commit }) {
    const response = await TicketsAPI.getContacts();
    const { contacts } = response.data;
    commit(types.SET_TICKET_CONTACTS, contacts || []);
  },

  async getSlaPolicies({ commit }) {
    const response = await TicketsAPI.getSlaPolicies();
    const { sla_policies } = response.data;
    commit(types.SET_TICKET_SLA_POLICIES, sla_policies || []);
  },

  async changeSlaPolicy({ commit }, { id, newSlaPolicy }) {
    const response = await TicketsAPI.changeSlaPolicy(id, newSlaPolicy);
    const { ticket } = response.data;
    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async removeSlaPolicy({ commit }, { id }) {
    const response = await TicketsAPI.removeSlaPolicy(id);
    const { ticket } = response.data;
    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async getSlaMonitoringData({ commit }, filters = {}) {
    const response = await TicketsAPI.getSlaMonitoringData(filters);
    const data = response.data;
    commit(types.SET_SLA_MONITORING_DATA, data);
    return data;
  },

  setFilters({ commit }, payload) {
    if (payload && payload.filters && payload.view) {
      commit(types.SET_TICKET_FILTERS, payload);
      return;
    }
    commit(types.SET_TICKET_FILTERS, { view: 'list', filters: payload || {} });
  },

  clearFilters({ commit }, payload = {}) {
    const view = normalizeView(payload?.view);
    commit(types.CLEAR_TICKET_FILTERS, { view });
  },

  async saveFiltersToBackend(
    { rootGetters, state: ticketState },
    payload = {}
  ) {
    try {
      const isLegacyPayload = typeof payload === 'string';
      const view = normalizeView(payload?.view);
      const searchQuery = isLegacyPayload
        ? payload
        : (payload?.searchQuery ?? '');
      const currentUISettings = rootGetters.getUISettings || {};
      const targetFilters =
        ticketState.filtersByView?.[view] || createDefaultFilters();
      const persistedFilters = {
        filters: targetFilters,
        searchQuery: searchQuery || '',
      };
      const viewSettingKey =
        view === 'kanban' ? 'ticketKanbanFilters' : 'ticketListFilters';

      const updatedUISettings = {
        ...currentUISettings,
        [viewSettingKey]: persistedFilters,
        ...(view === 'list' ? { ticketFilters: persistedFilters } : {}),
      };

      await authAPI.updateUISettings({ uiSettings: updatedUISettings });
    } catch (error) {
      // Error is handled silently as this is a background operation
    }
  },

  async loadFiltersFromBackend({ commit, rootGetters }, payload = {}) {
    try {
      const view = normalizeView(payload?.view);
      const viewSettingKey =
        view === 'kanban' ? 'ticketKanbanFilters' : 'ticketListFilters';
      const response = await authAPI.getProfile();
      const user = response.data;
      const uiSettings = user.ui_settings || {};
      const savedFilters =
        uiSettings[viewSettingKey] ||
        (view === 'list' ? uiSettings.ticketFilters : null);

      if (user) {
        commit(types.SET_CURRENT_USER, user, { root: true });
      }

      if (savedFilters && savedFilters.filters) {
        commit(types.SET_TICKET_FILTERS, {
          view,
          filters: savedFilters.filters,
        });
        return {
          filters: savedFilters.filters,
          searchQuery: savedFilters.searchQuery || '',
        };
      }

      return { filters: null, searchQuery: '' };
    } catch (error) {
      try {
        const view = normalizeView(payload?.view);
        const viewSettingKey =
          view === 'kanban' ? 'ticketKanbanFilters' : 'ticketListFilters';
        const uiSettings = rootGetters.getUISettings || {};
        const savedFilters =
          uiSettings[viewSettingKey] ||
          (view === 'list' ? uiSettings.ticketFilters : null);

        if (savedFilters && savedFilters.filters) {
          commit(types.SET_TICKET_FILTERS, {
            view,
            filters: savedFilters.filters,
          });
          return {
            filters: savedFilters.filters,
            searchQuery: savedFilters.searchQuery || '',
          };
        }
      } catch (fallbackError) {
        // Error loading ticket filters (fallback) - silently fail
      }

      return { filters: null, searchQuery: '' };
    }
  },

  async getTicket({ commit }, id) {
    commit(types.SET_TICKETS_UI_FLAG, { isFetchingTicket: true });
    try {
      const response = await TicketsAPI.getTicket(id);
      const { ticket } = response.data;
      commit(types.SET_TICKET, ticket);
      return ticket;
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isFetchingTicket: false });
    }
  },

  async getTicketHistory({ commit }, id) {
    commit(types.SET_TICKETS_UI_FLAG, { isFetchingHistory: true });
    try {
      const response = await TicketsAPI.getTicketHistory(id);
      const { histories } = response.data;
      commit(types.SET_TICKET_HISTORY, histories || []);
      return histories || [];
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isFetchingHistory: false });
    }
  },

  async create({ commit }, ticketData) {
    commit(types.SET_TICKETS_UI_FLAG, { isCreating: true });
    try {
      const response = await TicketsAPI.create(ticketData);
      const { ticket } = response.data;

      commit(types.ADD_TICKET, ticket);
      return ticket;
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isCreating: false });
    }
  },

  async delete({ commit }, id) {
    const response = await TicketsAPI.delete(id);
    const { ticket } = response.data;
    commit(types.DELETE_TICKET, ticket);
    return ticket;
  },

  async update({ commit }, { id, data }) {
    commit(types.SET_TICKETS_UI_FLAG, { isUpdating: true });
    try {
      const response = await TicketsAPI.update(id, data);
      const { ticket } = response.data;

      commit(types.UPDATE_TICKET, ticket);
      return ticket;
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isUpdating: false });
    }
  },

  async changeStatus({ commit }, { id, newStatus }) {
    const response = await TicketsAPI.changeStatus(id, newStatus);
    const { ticket } = response.data;

    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async changePriority({ commit }, { id, newPriority }) {
    const response = await TicketsAPI.changePriority(id, newPriority);
    const { ticket } = response.data;

    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async addComment(_context, { id, content, attachments = [] }) {
    const response = await TicketsAPI.addComment(id, content, attachments);
    return response.data;
  },

  async changeAssignee({ commit }, { id, newAssignee }) {
    const response = await TicketsAPI.change_assignee(id, newAssignee);
    const { ticket } = response.data;
    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async changeContact({ commit }, { id, newContact }) {
    const response = await TicketsAPI.changeContact(id, newContact);
    const { ticket } = response.data;
    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async changeTeam({ commit }, { id, newTeam }) {
    const response = await TicketsAPI.changeTeam(id, newTeam);
    const { ticket } = response.data;
    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async changeBoard({ commit }, { id, boardId, boardColumnId }) {
    const response = await TicketsAPI.changeBoard(id, boardId, boardColumnId);
    const { ticket } = response.data;
    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async removeAssignee({ commit }, { id }) {
    const response = await TicketsAPI.removeAssignee(id);
    const { ticket } = response.data;
    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async removeTeam({ commit }, { id }) {
    const response = await TicketsAPI.removeTeam(id);
    const { ticket } = response.data;
    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async removeContact({ commit }, { id }) {
    const response = await TicketsAPI.removeContact(id);
    const { ticket } = response.data;
    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async changeBusinessAccount({ commit }, { id, newBusinessAccount }) {
    const response = await TicketsAPI.changeBusinessAccount(
      id,
      newBusinessAccount
    );
    const { ticket } = response.data;
    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async removeBusinessAccount({ commit }, { id }) {
    const response = await TicketsAPI.removeBusinessAccount(id);
    const { ticket } = response.data;
    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async updateCustomAttributes({ commit }, { id, customAttributes }) {
    commit(types.SET_TICKETS_UI_FLAG, { isUpdating: true });
    try {
      const response = await TicketsAPI.updateCustomAttributes(
        id,
        customAttributes
      );
      const { ticket } = response.data;

      commit(types.UPDATE_TICKET, ticket);
      return ticket;
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isUpdating: false });
    }
  },

  async deleteCustomAttribute({ commit }, { id, attributeKey }) {
    commit(types.SET_TICKETS_UI_FLAG, { isUpdating: true });
    try {
      const response = await TicketsAPI.deleteCustomAttribute(id, attributeKey);
      const { ticket } = response.data;

      commit(types.UPDATE_TICKET, ticket);
      return ticket;
    } finally {
      commit(types.SET_TICKETS_UI_FLAG, { isUpdating: false });
    }
  },

  async changeTitle({ commit }, { id, newTitle }) {
    const response = await TicketsAPI.changeTitle(id, newTitle);
    const { ticket } = response.data;

    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async changeDescription({ commit }, { id, newDescription }) {
    const response = await TicketsAPI.changeDescription(id, newDescription);
    const { ticket } = response.data;

    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  async changeService({ commit }, { id, serviceId, subServiceId }) {
    const response = await TicketsAPI.changeService(
      id,
      serviceId,
      subServiceId
    );
    const { ticket } = response.data;
    commit(types.UPDATE_TICKET, ticket);
    return response.data;
  },

  clearTickets({ commit }) {
    commit(types.CLEAR_TICKETS);
  },
};

export const mutations = {
  [types.SET_TICKETS](_state, tickets) {
    _state.tickets = tickets;
  },

  [types.SET_TICKET](_state, ticket) {
    // Create a new object to ensure Vue reactivity
    _state.currentTicket = ticket ? { ...ticket } : null;
  },

  [types.SET_TICKET_STATUSES](_state, statuses) {
    _state.statuses = statuses;
  },

  [types.SET_TICKET_PRIORITIES](_state, priorities) {
    _state.priorities = priorities;
  },

  [types.SET_TICKET_TEAMS](_state, teams) {
    _state.teams = teams;
  },

  [types.SET_TICKET_USERS](_state, users) {
    _state.users = users;
  },

  [types.SET_TICKET_CONTACTS](_state, contacts) {
    _state.contacts = contacts;
  },

  [types.SET_TICKET_SLA_POLICIES](_state, slaPolicies) {
    _state.slaPolicies = slaPolicies;
  },

  [types.SET_SLA_MONITORING_DATA](_state, data) {
    _state.slaMonitoringData = data;
  },

  [types.SET_TICKET_FILTERS](_state, payload) {
    const view = normalizeView(payload?.view);
    const filters = payload?.filters || {};
    const currentViewFilters =
      _state.filtersByView[view] || createDefaultFilters();

    // Merge filters, but handle empty custom_attributes object specially
    const mergedFilters = { ...currentViewFilters, ...filters };

    // If custom_attributes is an empty object, delete it
    if (
      mergedFilters.custom_attributes &&
      Object.keys(mergedFilters.custom_attributes).length === 0
    ) {
      delete mergedFilters.custom_attributes;
    }

    _state.filtersByView = {
      ..._state.filtersByView,
      [view]: mergedFilters,
    };

    if (view === 'list') {
      _state.filters = mergedFilters;
    }
  },

  [types.CLEAR_TICKET_FILTERS](_state, payload = {}) {
    const view = normalizeView(payload?.view);
    const clearedFilters = createDefaultFilters();
    _state.filtersByView = {
      ..._state.filtersByView,
      [view]: clearedFilters,
    };

    if (view === 'list') {
      _state.filters = clearedFilters;
    }
  },

  [types.SET_TICKET_HISTORY](_state, history) {
    _state.ticketHistory = history;
  },

  [types.SET_TICKETS_META](_state, meta) {
    if (meta && typeof meta === 'object') {
      _state.meta = { ..._state.meta, ...meta };
    }
  },

  [types.ADD_TICKET](_state, ticket) {
    _state.tickets.unshift(ticket);
  },

  [types.DELETE_TICKET](_state, ticket) {
    const index = _state.tickets.findIndex(t => t.id === ticket.id);
    if (index !== -1) {
      _state.tickets.splice(index, 1);
    }
  },

  [types.UPDATE_TICKET](_state, updatedTicket) {
    const index = _state.tickets.findIndex(
      ticket => ticket.id === updatedTicket.id
    );
    if (index !== -1) {
      // Create a new object to ensure Vue reactivity
      _state.tickets.splice(index, 1, { ...updatedTicket });
    }

    if (_state.currentTicket && _state.currentTicket.id === updatedTicket.id) {
      // Create a new object to ensure Vue reactivity
      _state.currentTicket = { ...updatedTicket };
    }
  },

  [types.UPDATE_TICKET_STATUS](_state, { id, status_name, status_id, status_colors }) {
    const ticketIndex = _state.tickets.findIndex(ticket => ticket.id === id);
    if (ticketIndex !== -1) {
      // Create a new object to ensure Vue reactivity
      const updatedTicket = {
        ..._state.tickets[ticketIndex],
        status_name,
        status_id,
      };
      // Update status_colors if provided
      if (status_colors) {
        updatedTicket.status_colors = status_colors;
      }
      _state.tickets[ticketIndex] = updatedTicket;
    }

    if (_state.currentTicket && _state.currentTicket.id === id) {
      // Create a new object to ensure Vue reactivity
      const updatedCurrentTicket = {
        ..._state.currentTicket,
        status_name,
        status_id,
      };
      // Update status_colors if provided
      if (status_colors) {
        updatedCurrentTicket.status_colors = status_colors;
      }
      _state.currentTicket = updatedCurrentTicket;
    }
  },

  [types.UPDATE_TICKET_PRIORITY](_state, { id, priority_name, priority_id }) {
    const ticketIndex = _state.tickets.findIndex(ticket => ticket.id === id);
    if (ticketIndex !== -1) {
      // Create a new object to ensure Vue reactivity
      _state.tickets[ticketIndex] = {
        ..._state.tickets[ticketIndex],
        priority_name,
        priority_id,
      };
    }

    if (_state.currentTicket && _state.currentTicket.id === id) {
      // Create a new object to ensure Vue reactivity
      _state.currentTicket = {
        ..._state.currentTicket,
        priority_name,
        priority_id,
      };
    }
  },

  [types.UPDATE_TICKET_ASSIGNEE](_state, { id, assignee_name, assignee_id }) {
    const ticketIndex = _state.tickets.findIndex(ticket => ticket.id === id);
    if (ticketIndex !== -1) {
      // Create a new object to ensure Vue reactivity
      _state.tickets[ticketIndex] = {
        ..._state.tickets[ticketIndex],
        assignee_name,
        assignee_id,
      };
    }
    if (_state.currentTicket && _state.currentTicket.id === id) {
      // Create a new object to ensure Vue reactivity
      _state.currentTicket = {
        ..._state.currentTicket,
        assignee_name,
        assignee_id,
      };
    }
  },

  [types.UPDATE_TICKET_CONTACT](_state, { id, contact_name, contact_id }) {
    const ticketIndex = _state.tickets.findIndex(ticket => ticket.id === id);
    if (ticketIndex !== -1) {
      // Create a new object to ensure Vue reactivity
      _state.tickets[ticketIndex] = {
        ..._state.tickets[ticketIndex],
        contact_name,
        contact_id,
      };
    }
    if (_state.currentTicket && _state.currentTicket.id === id) {
      // Create a new object to ensure Vue reactivity
      _state.currentTicket = {
        ..._state.currentTicket,
        contact_name,
        contact_id,
      };
    }
  },

  [types.REMOVE_TICKET_CONTACT](_state, { id }) {
    const ticketIndex = _state.tickets.findIndex(ticket => ticket.id === id);
    if (ticketIndex !== -1) {
      // Create a new object to ensure Vue reactivity
      _state.tickets[ticketIndex] = {
        ..._state.tickets[ticketIndex],
        contact_name: null,
        contact_id: null,
      };
    }
    if (_state.currentTicket && _state.currentTicket.id === id) {
      // Create a new object to ensure Vue reactivity
      _state.currentTicket = {
        ..._state.currentTicket,
        contact_name: null,
        contact_id: null,
      };
    }
  },

  [types.UPDATE_TICKET_TEAM](_state, { id, team_name, team_id }) {
    const ticketIndex = _state.tickets.findIndex(ticket => ticket.id === id);
    if (ticketIndex !== -1) {
      // Create a new object to ensure Vue reactivity
      _state.tickets[ticketIndex] = {
        ..._state.tickets[ticketIndex],
        team_name,
        team_id,
      };
    }
    if (_state.currentTicket && _state.currentTicket.id === id) {
      // Create a new object to ensure Vue reactivity
      _state.currentTicket = {
        ..._state.currentTicket,
        team_name,
        team_id,
      };
    }
  },

  [types.UPDATE_TICKET_SLA_POLICY](
    _state,
    { id, sla_policy_name, sla_policy_id }
  ) {
    const ticketIndex = _state.tickets.findIndex(ticket => ticket.id === id);
    if (ticketIndex !== -1) {
      // Create a new object to ensure Vue reactivity
      _state.tickets[ticketIndex] = {
        ..._state.tickets[ticketIndex],
        sla_policy_name,
        sla_policy_id,
      };
    }

    if (_state.currentTicket && _state.currentTicket.id === id) {
      // Create a new object to ensure Vue reactivity
      _state.currentTicket = {
        ..._state.currentTicket,
        sla_policy_name,
        sla_policy_id,
      };
    }
  },

  [types.UPDATE_TICKET_BUSINESS_ACCOUNT](
    _state,
    { id, business_account_name, business_account_id }
  ) {
    const ticketIndex = _state.tickets.findIndex(ticket => ticket.id === id);
    if (ticketIndex !== -1) {
      // Create a new object to ensure Vue reactivity
      _state.tickets[ticketIndex] = {
        ..._state.tickets[ticketIndex],
        business_account_name,
        business_account_id,
      };
    }

    if (_state.currentTicket && _state.currentTicket.id === id) {
      // Create a new object to ensure Vue reactivity
      _state.currentTicket = {
        ..._state.currentTicket,
        business_account_name,
        business_account_id,
      };
    }
  },

  [types.CLEAR_TICKETS](_state) {
    _state.tickets = [];
    _state.currentTicket = null;
    _state.ticketHistory = [];
    _state.meta = {
      count: 0,
      currentPage: 1,
      totalPages: 1,
    };
  },

  [types.SET_TICKETS_UI_FLAG](_state, uiFlag) {
    _state.uiFlags = { ..._state.uiFlags, ...uiFlag };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
